import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, FileSearch, Globe, Filter, Building2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function EuTenderMonitoring() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="EU Tender Monitoring"
        description="Monitor EU public tenders across all EU countries. Track TED notices, filter by CPV codes, and receive alerts with TED Monitor."
        path="/eu-tender-monitoring"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/eu-tender-monitoring" },
          { lang: "it", href: "https://tedmonitor.eu/it/monitoraggio-bandi-ue" },
          { lang: "x-default", href: "https://tedmonitor.eu/eu-tender-monitoring" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              EU Tender Monitoring Across All EU Countries
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Public procurement opportunities are published every day across multiple countries, contracting
              authorities, languages, and procurement procedures. TED Monitor helps you monitor EU tenders in a
              structured and centralized way, based on official European data.
            </p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/ted-procurement-alerts"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                TED procurement alerts
              </Link>
              <Link
                to="/european-public-procurement-software"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                European public procurement software
              </Link>
              <Link
                to="/multilingual-access"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Multilingual access (24 EU languages)
              </Link>
              <Link
                to="/compliance-and-security"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Compliance and security
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>
          </div>
        </section>

        {/* What is EU Tender Monitoring */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileSearch className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">What is EU tender monitoring</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              EU tender monitoring is the systematic tracking of public tenders published by contracting authorities
              across European Union countries. It helps organizations identify relevant opportunities, reduce noise, and
              react quickly to new publications.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">It typically includes:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Contract notices and procurement announcements",
                "Above-threshold EU tenders",
                "Open, restricted, and negotiated procedures",
                "Contract award notices and results",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor relies on official EU sources to provide reliable, continuously updated tender data for
              monitoring and analysis.
            </p>
          </div>
        </section>

        {/* Official EU Tender Data from TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Official EU tender data from TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              All monitored tenders come from{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              , the official supplement to the Journal of the European Union dedicated to public procurement.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">With TED Monitor you can:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Access tenders published across all EU Member States",
                "Consult results in a structured and comparable format",
                "Reduce fragmentation across national procurement portals",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Learn more about our data source →
              </Link>
            </p>
          </div>
        </section>

        {/* Coverage Across All EU Countries */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Building2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Coverage across all EU countries</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor covers EU Member States to help you build a unified view of the European public procurement
              market. This is especially valuable for cross-border expansion, partner scouting, and tender pipeline
              development.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Monitoring includes:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Central and local contracting authorities",
                "Public bodies and bodies governed by public law",
                "Classic sectors and special sectors (utilities, transport, etc.)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Monitor Tenders by CPV Codes */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Monitor EU tenders by CPV codes</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              EU tenders are classified by CPV (Common Procurement Vocabulary). CPV codes allow you to identify
              procurement categories precisely and run targeted monitoring.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">With TED Monitor you can:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Filter tenders by CPV code",
                "Focus on your target procurement categories",
                "Reduce irrelevant results and improve signal-to-noise ratio",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              This enables relevant monitoring even in highly competitive procurement markets.
            </p>
          </div>
        </section>

        {/* Tender Alerts */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Tender alerts for real-time monitoring
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Effective monitoring requires speed. TED Monitor lets you configure monitoring criteria and receive
              notifications when new tenders match your requirements.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Alerts can be configured based on:</p>
            <div className="flex flex-wrap gap-2">
              {["CPV", "Country", "Procedure type", "Estimated value"].map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <p className="mt-4">
              <Link to="/ted-procurement-alerts" className="text-primary hover:underline font-medium text-sm">
                Explore TED procurement alerts →
              </Link>
            </p>
          </div>
        </section>

        {/* Why Use TED Monitor */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
              Why use TED Monitor for EU tender monitoring
            </h2>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Compared to manual consultation of TED or fragmented national portals, TED Monitor provides a structured
              workflow for building a tender pipeline and tracking opportunities systematically.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "A unified view of EU tenders",
                "Advanced filters for relevant results",
                "Structured and comparable data",
                "Operational time savings",
                "Broader coverage of opportunities",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground mb-3 text-sm">Designed for:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["Companies", "Consultants", "Professional firms", "Procurement teams"].map((item) => (
                <div key={item} className="p-2 bg-background rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to="/european-public-procurement-software" className="text-primary hover:underline font-medium">
                European public procurement software →
              </Link>
              <Link to="/multilingual-access" className="text-primary hover:underline font-medium">
                Multilingual access →
              </Link>
              <Link to="/compliance-and-security" className="text-primary hover:underline font-medium">
                Compliance and security →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Start monitoring EU tenders</h2>
            <p className="text-base mb-6 opacity-90">
              Build a reliable tender pipeline with structured monitoring, official TED data, and alert-based workflows.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Data Source
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Terms
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
